import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Loader2,
  RefreshCw,
  TrendingUp,
  Package,
  Coins,
  AlertTriangle,
  CalendarDays,
  Boxes,
  Search,
  Cog,
  Eye,
  Users,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  VisualInventoryModal,
  CharactersPanel,
} from "./AdminOreStoreCharacterInventory";
import { AdminOreStoreSchedulePanel } from "./AdminOreStoreSchedulePanel";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const ORESTORE_LOGO =
  "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/3azwctos_Ore-Store-LOGO2.png";

const CATEGORY_COLORS = {
  Ores: "#e07a1f",
  Plates: "#3aa3d9",
  Crystals: "#c64bd2",
  Special: "#ffd84d",
  Uncategorized: "#888888",
};

const formatMesos = (n) => {
  const v = Math.floor(Number(n) || 0);
  return v.toLocaleString("en-US").replace(/,/g, ".");
};

const formatShortMesos = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(2) + "B";
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "k";
  return String(v);
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// =========================== KPI CARD ===========================
const KpiCard = ({ icon: Icon, label, value, sub, accent = "text-amber-300", testId }) => (
  <div
    className="rounded-lg border-2 border-[#5a3a14] bg-gradient-to-br from-[#3a2814] to-[#2a1a0e] p-3 shadow-[0_3px_0_#1a0e05]"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-orange-200/80 font-bold">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className={`mt-1 text-xl sm:text-2xl font-extrabold font-mono ${accent}`} style={{ textShadow: "2px 2px 0 #1a0e05" }}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-orange-200/70 mt-0.5">{sub}</div>}
  </div>
);

// =========================== STOCK PANEL ===========================
const StockPanel = ({ stock, loading, onRefresh, lowThreshold, onPickItem }) => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("stock_asc");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showVisualInventory, setShowVisualInventory] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = stock?.items || [];
    if (categoryFilter !== "all") list = list.filter((it) => it.category === categoryFilter);
    if (q) list = list.filter((it) => it.name.toLowerCase().includes(q));
    const sorters = {
      stock_asc: (a, b) => a.stock - b.stock,
      stock_desc: (a, b) => b.stock - a.stock,
      qty_desc: (a, b) => b.qty_30d - a.qty_30d,
      rev_desc: (a, b) => b.rev_30d - a.rev_30d,
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return [...list].sort(sorters[sortBy] || sorters.stock_asc);
  }, [stock, search, sortBy, categoryFilter]);

  if (loading && !stock) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const totals = stock?.totals || {};
  const cats = stock?.categories || [];
  const history = stock?.history || [];
  const historyForChart = history.map((h) => ({
    label: h.date.slice(5),
    sellM: (h.value_at_sell || 0) / 1_000_000,
    buyM: (h.value_at_buy || 0) / 1_000_000,
    units: h.stock_units || 0,
  }));

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          icon={Boxes}
          label="Stock Units"
          value={formatMesos(totals.stock_units)}
          sub="across all items"
          accent="text-amber-300"
          testId="stock-kpi-units"
        />
        <KpiCard
          icon={Coins}
          label="Stock Value (sell)"
          value={formatShortMesos(totals.stock_value_at_sell) + " m"}
          sub="at current sell price"
          accent="text-yellow-300"
          testId="stock-kpi-value"
        />
        <KpiCard
          icon={Coins}
          label="Stock Value (buy)"
          value={formatShortMesos(totals.stock_value_at_buy) + " m"}
          sub="our cost (sourcing)"
          accent="text-emerald-300"
          testId="stock-kpi-buy-value"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Low stock"
          value={totals.low_stock || 0}
          sub={`≤ ${lowThreshold || 50} units`}
          accent="text-orange-300"
          testId="stock-kpi-low"
        />
        <KpiCard
          icon={Package}
          label="Out of stock"
          value={totals.out_of_stock || 0}
          sub="0 units"
          accent="text-red-400"
          testId="stock-kpi-out"
        />
      </div>

      {/* Per-category breakdown */}
      {cats.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stock-cat-breakdown">
          {cats.map((c) => {
            const color = CATEGORY_COLORS[c.category] || "#888";
            return (
              <div
                key={c.category}
                className="rounded-lg border-2 bg-[#1f1208] p-3 shadow-[0_3px_0_#1a0e05]"
                style={{ borderColor: color }}
                data-testid={`stock-cat-${c.category}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-extrabold uppercase tracking-wider text-sm" style={{ color }}>
                    {c.category}
                  </div>
                  <span className="text-[10px] text-orange-300/70">{c.items} item{c.items === 1 ? "" : "s"}</span>
                </div>
                <div className="mt-1 text-2xl font-extrabold font-mono text-amber-300">
                  {formatMesos(c.units)}
                  <span className="ml-1 text-xs text-orange-200/70 font-normal">units</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px] font-mono">
                  <span className="text-orange-200/70">Sell value</span>
                  <span className="text-right text-yellow-300 font-bold">{formatShortMesos(c.value_at_sell)}m</span>
                  <span className="text-orange-200/70">Buy value</span>
                  <span className="text-right text-emerald-300 font-bold">{formatShortMesos(c.value_at_buy)}m</span>
                  <span className="text-orange-200/70">30d qty</span>
                  <span className="text-right">{formatMesos(c.qty_30d)}</span>
                  <span className="text-orange-200/70">7d qty</span>
                  <span className="text-right">{formatMesos(c.qty_7d)}</span>
                </div>
                {(c.low_stock || c.out_of_stock) ? (
                  <div className="text-[10px] mt-1.5">
                    {c.low_stock ? <span className="text-orange-300 mr-2">⚠ {c.low_stock} low</span> : null}
                    {c.out_of_stock ? <span className="text-red-400">✗ {c.out_of_stock} OOS</span> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Stock value history */}
      {historyForChart.length > 1 && (
        <Card className="bg-[#2a1a0e] border-2 border-[#5a3a14]">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-200 text-base">Stock value history (M mesos)</CardTitle>
            <CardDescription>Daily snapshot — sell-side and buy-side total stock value</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={historyForChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#5a3a14" />
                  <XAxis dataKey="label" stroke="#e1b066" fontSize={10} />
                  <YAxis stroke="#e1b066" fontSize={10} tickFormatter={(v) => v.toFixed(0)} />
                  <Tooltip contentStyle={{ background: "#3a2814", border: "1px solid #5a3a14", color: "#fff" }} />
                  <Legend />
                  <Line type="monotone" dataKey="sellM" stroke="#ffae00" strokeWidth={2} name="Sell value" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="buyM" stroke="#33c47f" strokeWidth={2} name="Buy value" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search + sort */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="stock-search-input"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-9 px-2 rounded border border-input bg-background text-sm"
          data-testid="stock-cat-filter"
        >
          <option value="all">All categories</option>
          <option value="Ores">Ores</option>
          <option value="Plates">Plates</option>
          <option value="Crystals">Crystals</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="h-9 px-2 rounded border border-input bg-background text-sm"
          data-testid="stock-sort"
        >
          <option value="stock_asc">Stock ↑ (low first)</option>
          <option value="stock_desc">Stock ↓ (high first)</option>
          <option value="qty_desc">Sales 30d (qty)</option>
          <option value="rev_desc">Revenue 30d</option>
          <option value="name">Name A→Z</option>
        </select>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() => setShowVisualInventory(true)}
          data-testid="stock-visual-inventory-btn"
          className="bg-amber-600 hover:bg-amber-500 text-white"
        >
          <Eye className="w-4 h-4 mr-1" /> Characters Stock
        </Button>
      </div>

      <VisualInventoryModal
        open={showVisualInventory}
        onClose={() => setShowVisualInventory(false)}
      />

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-[#5a3a14]">
        <table className="w-full text-sm">
          <thead className="bg-[#3a2814] text-orange-200">
            <tr>
              <th className="text-left px-3 py-2 font-bold">Item</th>
              <th className="text-right px-3 py-2 font-bold">Stock</th>
              <th className="text-right px-3 py-2 font-bold">Sell</th>
              <th className="text-right px-3 py-2 font-bold">30d qty</th>
              <th className="text-right px-3 py-2 font-bold">30d rev</th>
              <th className="text-right px-3 py-2 font-bold">Avg/day</th>
              <th className="text-right px-3 py-2 font-bold">Days left</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => {
              const rowClass = it.is_out
                ? "bg-red-950/30"
                : it.is_low
                ? "bg-orange-950/30"
                : "bg-card/30 hover:bg-card/60";
              return (
                <tr
                  key={it.id}
                  className={`border-t border-[#5a3a14]/50 cursor-pointer ${rowClass}`}
                  data-testid={`stock-row-${it.name}`}
                  onClick={() => onPickItem && onPickItem(it.name)}
                  title="Click for full details"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {it.image_url && (
                        <img
                          src={it.image_url}
                          alt=""
                          className="w-7 h-7 object-contain"
                          style={{ imageRendering: "pixelated" }}
                        />
                      )}
                      <div>
                        <div className="font-bold">{it.name}</div>
                        <div className="text-[10px]">
                          <Badge
                            variant="outline"
                            style={{ borderColor: CATEGORY_COLORS[it.category] || "#666", color: CATEGORY_COLORS[it.category] || "#999" }}
                            className="text-[9px] px-1 py-0"
                          >
                            {it.category || "?"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold">
                    {it.is_out ? (
                      <Badge variant="destructive" className="text-[10px]">OOS</Badge>
                    ) : it.is_low ? (
                      <span className="text-orange-300">{formatMesos(it.stock)}</span>
                    ) : (
                      <span>{formatMesos(it.stock)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-yellow-200">
                    {formatMesos(it.sell_price)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {it.qty_30d > 0 ? it.qty_30d : <span className="opacity-40">0</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {it.rev_30d > 0 ? formatShortMesos(it.rev_30d) : <span className="opacity-40">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {it.avg_per_day > 0 ? it.avg_per_day : <span className="opacity-40">0</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {it.days_until_oos != null ? (
                      <span className={it.days_until_oos < 7 ? "text-red-300 font-bold" : it.days_until_oos < 30 ? "text-orange-300" : "text-emerald-300"}>
                        {it.days_until_oos}d
                      </span>
                    ) : (
                      <span className="opacity-40">∞</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =========================== SALES PANEL ===========================
const SalesPanel = ({ summary, loading, onChangeRange, fromDate, toDate, onRefresh, onPickItem, categoryFilter, setCategoryFilter }) => {
  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  // Apply category filter on top items + recompute totals + daily series + cat list
  const totals0 = summary?.totals || {};
  const baseTopItems = summary?.top_items || [];
  const baseDaily = summary?.daily || [];
  const filteredTop = categoryFilter === "all"
    ? baseTopItems
    : baseTopItems.filter((t) => (t.category || "Uncategorized") === categoryFilter);

  // When filtering, recompute totals + daily from filtered items only
  const isFiltered = categoryFilter !== "all";
  let totals = totals0;
  let daily = baseDaily;
  if (isFiltered) {
    const filteredNameSet = new Set(filteredTop.map((t) => t.name));
    const tx = filteredTop.reduce((a, t) => a + (t.tx || 0), 0);
    const qty = filteredTop.reduce((a, t) => a + (t.qty || 0), 0);
    const rev = filteredTop.reduce((a, t) => a + (t.rev || 0), 0);
    totals = {
      revenue: rev,
      quantity: qty,
      transactions: tx,
      days_with_sales: totals0.days_with_sales,
      total_days: totals0.total_days,
      avg_revenue_per_day: totals0.total_days ? Math.round(rev / totals0.total_days * 100) / 100 : 0,
      _filtered: true,
      _name_set: filteredNameSet,
    };
    // daily breakdown: backend doesn't return per-item daily; we just leave full series for now
  }

  const dailyForChart = (daily || []).map((d) => ({
    ...d,
    label: d.date.slice(5),
    revM: d.rev / 1_000_000,
  }));

  const top = filteredTop.slice(0, 10);
  const cats = (summary?.category_breakdown || []);
  const ranking = (summary?.ranking || []);
  const filteredRanking = categoryFilter === "all" ? ranking : ranking.filter((r) => (r.category || "Uncategorized") === categoryFilter);

  // Local search + sort for the full ranking table.
  // Uses an internal state hook closure (Sub-component below) — kept inline because
  // SalesPanel already owns the data; we just transform on render.

  const setQuickRange = (days) => {
    onChangeRange(isoDaysAgo(days - 1), todayISO());
  };
  const setToday = () => onChangeRange(todayISO(), todayISO());
  const setYesterday = () => onChangeRange(isoDaysAgo(1), isoDaysAgo(1));

  return (
    <div className="space-y-4">
      {/* Range controls */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <div className="text-[10px] uppercase font-bold text-orange-200/80">From</div>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => onChangeRange(e.target.value, toDate)}
            className="h-9"
            data-testid="sales-from-date"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold text-orange-200/80">To</div>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => onChangeRange(fromDate, e.target.value)}
            className="h-9"
            data-testid="sales-to-date"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          <Button variant="outline" size="sm" onClick={setToday} className="h-9" data-testid="sales-quick-today">Today</Button>
          <Button variant="outline" size="sm" onClick={setYesterday} className="h-9" data-testid="sales-quick-yesterday">Yesterday</Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange(3)} className="h-9" data-testid="sales-quick-3">Last 3d</Button>
          {[7, 30, 90, 365].map((d) => (
            <Button
              key={d}
              variant="outline"
              size="sm"
              onClick={() => setQuickRange(d)}
              className="h-9"
              data-testid={`sales-quick-${d}`}
            >
              {d}d
            </Button>
          ))}
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-9 px-2 rounded border border-input bg-background text-sm"
          data-testid="sales-cat-filter"
        >
          <option value="all">All categories</option>
          <option value="Ores">Ores</option>
          <option value="Plates">Plates</option>
          <option value="Crystals">Crystals</option>
        </select>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} className="h-9 ml-auto">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          icon={Coins}
          label="Revenue"
          value={formatShortMesos(totals.revenue) + " m"}
          sub={`${formatMesos(totals.revenue || 0)} mesos`}
          accent="text-yellow-300"
          testId="sales-kpi-revenue"
        />
        <KpiCard
          icon={Coins}
          label={`Tax (${summary?.tax_pct || 3}%)`}
          value={"−" + formatShortMesos(totals.tax) + " m"}
          sub={`net ${formatShortMesos(totals.revenue_after_tax)}m`}
          accent="text-rose-300"
          testId="sales-kpi-tax"
        />
        <KpiCard
          icon={Coins}
          label="Buy cost"
          value={"−" + formatShortMesos(totals.buy_cost) + " m"}
          sub="cost of items sold"
          accent="text-orange-300"
          testId="sales-kpi-buycost"
        />
        <KpiCard
          icon={TrendingUp}
          label="Profit (clear)"
          value={(totals.profit >= 0 ? "+" : "") + formatShortMesos(totals.profit) + " m"}
          sub={`${(totals.profit >= 0 ? "+" : "") + (totals.margin_pct || 0)}% margin`}
          accent={totals.profit >= 0 ? "text-emerald-300" : "text-red-400"}
          testId="sales-kpi-profit"
        />
        <KpiCard
          icon={Package}
          label="Items sold"
          value={formatMesos(totals.quantity)}
          sub={`${totals.transactions || 0} transactions`}
          accent="text-amber-300"
          testId="sales-kpi-qty"
        />
        <KpiCard
          icon={CalendarDays}
          label="Avg/day (profit)"
          value={formatShortMesos(totals.avg_profit_per_day) + " m"}
          sub={`${totals.days_with_sales || 0} active days`}
          accent="text-fuchsia-300"
          testId="sales-kpi-avg"
        />
      </div>

      {/* Per-category profit cards */}
      {cats.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sales-cat-cards">
          {cats.map((c) => {
            const color = CATEGORY_COLORS[c.category] || "#888";
            return (
              <div
                key={c.category}
                className="rounded-lg border-2 bg-[#1f1208] p-3 shadow-[0_3px_0_#1a0e05]"
                style={{ borderColor: color }}
                data-testid={`sales-cat-card-${c.category}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-extrabold uppercase tracking-wider text-sm" style={{ color }}>
                    {c.category}
                  </div>
                  <span className="text-[10px] text-orange-300/70">{c.items} item{c.items === 1 ? "" : "s"}</span>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
                  <span className="text-orange-200/70">Revenue</span>
                  <span className="text-right text-yellow-300 font-bold">{formatShortMesos(c.rev)}m</span>
                  <span className="text-orange-200/70">− Tax</span>
                  <span className="text-right text-rose-300">−{formatShortMesos(c.tax)}m</span>
                  <span className="text-orange-200/70">− Buy cost</span>
                  <span className="text-right text-orange-300">−{formatShortMesos(c.buy_cost)}m</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between border-t-2 pt-1.5" style={{ borderColor: color + "55" }}>
                  <span className="text-[11px] uppercase font-bold" style={{ color }}>Profit</span>
                  <span className={`font-extrabold font-mono ${c.profit >= 0 ? "text-emerald-300" : "text-red-400"}`}>
                    {(c.profit >= 0 ? "+" : "") + formatShortMesos(c.profit)}m
                    <span className="ml-1 text-[10px] opacity-70">({c.profit >= 0 ? "+" : ""}{c.margin_pct}%)</span>
                  </span>
                </div>
                <div className="text-[10px] text-orange-200/60 mt-1">
                  {formatMesos(c.qty)} sold • {c.tx} tx • avg {c.avg_per_day_qty}/day
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Daily revenue chart */}
      <Card className="bg-[#2a1a0e] border-2 border-[#5a3a14]">
        <CardHeader className="pb-2">
          <CardTitle className="text-orange-200 text-base">Daily Revenue (M mesos)</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={dailyForChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#5a3a14" />
                <XAxis dataKey="label" stroke="#e1b066" fontSize={10} />
                <YAxis stroke="#e1b066" fontSize={10} tickFormatter={(v) => v.toFixed(1)} />
                <Tooltip
                  contentStyle={{ background: "#3a2814", border: "1px solid #5a3a14", color: "#fff" }}
                  formatter={(v, n) => (n === "revM" ? [`${v.toFixed(2)} M mesos`, "Revenue"] : [v, n])}
                />
                <Bar dataKey="revM" fill="#ffae00" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Top items */}
        <Card className="bg-[#2a1a0e] border-2 border-[#5a3a14]">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-200 text-base">Top items by revenue</CardTitle>
            <CardDescription>Click an item to see its sales history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {top.length === 0 && (
                <div className="text-muted-foreground italic text-sm py-4 text-center">No sales in range.</div>
              )}
              {top.map((it, idx) => {
                const max = top[0]?.rev || 1;
                const pct = Math.max(2, Math.round((it.rev / max) * 100));
                const color = CATEGORY_COLORS[it.category] || "#888";
                return (
                  <button
                    type="button"
                    key={it.name}
                    onClick={() => onPickItem(it.name)}
                    className="w-full text-left rounded p-2 hover:bg-[#3a2814] transition flex items-center gap-2"
                    data-testid={`sales-top-item-${it.name}`}
                  >
                    <div className="text-[10px] font-bold w-5 text-orange-300/70">#{idx + 1}</div>
                    {it.image_url && (
                      <img src={it.image_url} alt="" className="w-6 h-6 object-contain" style={{ imageRendering: "pixelated" }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-bold truncate">{it.name}</span>
                        <span className="font-mono text-yellow-200 ml-2">{formatShortMesos(it.rev)} m</span>
                      </div>
                      <div className="h-1.5 rounded bg-[#1a0e05] overflow-hidden mt-1">
                        <div className="h-full rounded" style={{ width: pct + "%", background: color }} />
                      </div>
                      <div className="text-[10px] text-orange-200/70 mt-0.5">
                        {it.qty} sold • {it.tx} tx • Stock: <span className="font-mono">{formatMesos(it.stock)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Category breakdown */}
        <Card className="bg-[#2a1a0e] border-2 border-[#5a3a14]">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-200 text-base">Revenue by category</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={cats}
                    dataKey="rev"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={45}
                    label={(e) => `${e.category}: ${formatShortMesos(e.rev)}m`}
                    labelLine={false}
                  >
                    {cats.map((c) => (
                      <Cell key={c.category} fill={CATEGORY_COLORS[c.category] || "#888"} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip
                    contentStyle={{ background: "#3a2814", border: "1px solid #5a3a14", color: "#fff" }}
                    formatter={(v) => [`${formatMesos(v)} mesos`, "Revenue"]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Full ranking table with profit & margin */}
      <RankingTable
        rows={filteredRanking}
        onPickItem={onPickItem}
      />
    </div>
  );
};

// =========================== RANKING TABLE ===========================
const RANK_COLUMNS = [
  { key: "name",         label: "Item",     align: "left",  sortable: true },
  { key: "qty",          label: "Qty",      align: "right", sortable: true },
  { key: "avg_per_day_qty", label: "Avg/d", align: "right", sortable: true },
  { key: "tx",           label: "Tx",       align: "right", sortable: true },
  { key: "rev",          label: "Revenue",  align: "right", sortable: true },
  { key: "tax",          label: "Tax",      align: "right", sortable: true },
  { key: "buy_cost",     label: "Buy cost", align: "right", sortable: true },
  { key: "profit",       label: "Profit",   align: "right", sortable: true },
  { key: "margin_pct",   label: "Margin",   align: "right", sortable: true },
  { key: "stock",        label: "Stock",    align: "right", sortable: true },
  { key: "stock_value_sell", label: "Stock value (sell)", align: "right", sortable: true },
];

const RankingTable = ({ rows, onPickItem }) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("rev");
  const [sortDir, setSortDir] = useState("desc");

  const enriched = useMemo(
    () => rows.map((r) => ({ ...r, stock_value_sell: (r.stock || 0) * (r.sell_price || 0) })),
    [rows]
  );

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = enriched;
    if (q) list = list.filter((r) => (r.name || "").toLowerCase().includes(q));
    const dirMul = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string" || typeof bv === "string") {
        return String(av || "").localeCompare(String(bv || "")) * dirMul;
      }
      return ((Number(av) || 0) - (Number(bv) || 0)) * dirMul;
    });
    return list;
  }, [enriched, search, sortKey, sortDir]);

  const onHeaderClick = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  return (
    <Card className="bg-[#2a1a0e] border-2 border-[#5a3a14]" data-testid="sales-ranking-table">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-orange-200 text-base">Full sell ranking — by item</CardTitle>
            <CardDescription>
              {displayed.length} of {rows.length} item{rows.length === 1 ? "" : "s"} • click any column header to sort • click row for full history
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-orange-300/70" />
            <Input
              type="text"
              placeholder="Search ore by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              data-testid="sales-rank-search"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#3a2814] text-orange-200">
              <tr>
                <th className="text-left px-2 py-2 font-bold">#</th>
                {RANK_COLUMNS.map((c) => {
                  const isActive = c.key === sortKey;
                  const arrow = isActive ? (sortDir === "asc" ? " ▲" : " ▼") : "";
                  return (
                    <th
                      key={c.key}
                      className={`px-2 py-2 font-bold select-none ${c.align === "right" ? "text-right" : "text-left"} ${c.sortable ? "cursor-pointer hover:text-yellow-300" : ""} ${isActive ? "text-yellow-300" : ""}`}
                      onClick={() => c.sortable && onHeaderClick(c.key)}
                      data-testid={`sales-rank-header-${c.key}`}
                    >
                      {c.label}{arrow}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayed.map((it, idx) => (
                <tr
                  key={it.name}
                  onClick={() => onPickItem(it.name)}
                  className="border-t border-[#5a3a14]/50 hover:bg-[#3a2814] cursor-pointer"
                  data-testid={`sales-rank-row-${it.name}`}
                >
                  <td className="px-2 py-1.5 text-orange-300/70 font-mono">{idx + 1}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      {it.image_url && (
                        <img src={it.image_url} alt="" className="w-6 h-6 object-contain" style={{ imageRendering: "pixelated" }} />
                      )}
                      <div>
                        <div className="font-bold">{it.name}</div>
                        <Badge
                          variant="outline"
                          style={{ borderColor: CATEGORY_COLORS[it.category] || "#666", color: CATEGORY_COLORS[it.category] || "#999" }}
                          className="text-[9px] px-1 py-0"
                        >
                          {it.category || "?"}
                        </Badge>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{formatMesos(it.qty)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-orange-200/80">{it.avg_per_day_qty}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{it.tx}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-yellow-300">{formatShortMesos(it.rev)}m</td>
                  <td className="px-2 py-1.5 text-right font-mono text-rose-300">{it.tax ? "−" + formatShortMesos(it.tax) + "m" : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-orange-300">{it.buy_cost ? "−" + formatShortMesos(it.buy_cost) + "m" : "—"}</td>
                  <td className={`px-2 py-1.5 text-right font-mono font-bold ${it.profit >= 0 ? "text-emerald-300" : "text-red-400"}`}>
                    {it.qty ? (it.profit >= 0 ? "+" : "") + formatShortMesos(it.profit) + "m" : "—"}
                  </td>
                  <td className={`px-2 py-1.5 text-right font-mono ${it.margin_pct >= 0 ? "text-emerald-300" : "text-red-400"}`}>
                    {it.qty ? (it.margin_pct >= 0 ? "+" : "") + it.margin_pct + "%" : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-orange-200/80">{formatMesos(it.stock)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-yellow-200/80">{formatShortMesos(it.stock_value_sell)}m</td>
                </tr>
              ))}
              {displayed.length === 0 && (
                <tr>
                  <td colSpan={RANK_COLUMNS.length + 1} className="text-center py-6 text-muted-foreground italic">
                    {search ? `No item matches "${search}".` : "No items in selected range / category."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
const ItemHistoryPanel = ({ name, fromDate, toDate, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/orestore/sales/item`, {
        params: { name, from_date: fromDate, to_date: toDate },
        headers: getAuthHeaders(),
      });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load item history");
    } finally {
      setLoading(false);
    }
  }, [name, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card className="bg-[#2a1a0e] border-2 border-[#5a3a14]">
        <CardContent className="py-10 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const series = (data.daily || []).map((d) => ({ ...d, label: d.date.slice(5) }));
  const T = data.totals || {};
  const it = data.item || {};
  const priceHistory = data.price_history || [];

  // Active special detection (server already sends raw fields; we re-check in UI)
  const specialUntilDate = it.special_price_until ? new Date(it.special_price_until) : null;
  const specialActive = it.special_price > 0 && specialUntilDate && specialUntilDate.getTime() > Date.now();
  const fmtUntil = (d) =>
    d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const StatBlock = ({ title, agg, accent = "text-yellow-300", testId }) => {
    const a = agg || {};
    return (
      <div className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] p-3" data-testid={testId}>
        <div className="text-[10px] uppercase font-bold tracking-wider text-orange-200/80">{title}</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono mt-1">
          <span className="text-orange-200/70">Sold</span>
          <span className="text-right text-amber-300 font-bold">{formatMesos(a.qty || 0)}</span>
          <span className="text-orange-200/70">Tx</span>
          <span className="text-right text-fuchsia-300">{a.tx || 0}</span>
          <span className="text-orange-200/70">Revenue</span>
          <span className={`text-right font-bold ${accent}`}>{formatShortMesos(a.rev || 0)}m</span>
          <span className="text-orange-200/70">− Tax</span>
          <span className="text-right text-rose-300">−{formatShortMesos(a.tax || 0)}m</span>
          <span className="text-orange-200/70">− Buy cost</span>
          <span className="text-right text-orange-300">−{formatShortMesos(a.buy_cost || 0)}m</span>
        </div>
        <div className="mt-1.5 flex items-center justify-between border-t-2 border-[#5a3a14] pt-1.5">
          <span className="text-[11px] uppercase font-bold text-orange-200">Profit</span>
          <span className={`font-extrabold font-mono ${(a.profit || 0) >= 0 ? "text-emerald-300" : "text-red-400"}`}>
            {((a.profit || 0) >= 0 ? "+" : "") + formatShortMesos(a.profit || 0)}m
            <span className="ml-1 text-[10px] opacity-70">
              ({(a.margin_pct || 0) >= 0 ? "+" : ""}{a.margin_pct || 0}%)
            </span>
          </span>
        </div>
      </div>
    );
  };

  return (
    <Card className="bg-[#2a1a0e] border-2 border-[#5a3a14]" data-testid="item-history-panel">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {it.image_url && (
              <img src={it.image_url} alt="" className="w-10 h-10 object-contain" style={{ imageRendering: "pixelated" }} />
            )}
            <div>
              <CardTitle className="text-orange-200 text-base">
                {it.name}
                {it.is_hardcoded_buy && (
                  <span className="ml-2 text-[10px] text-yellow-300 font-mono">[HARDCODED BUY]</span>
                )}
              </CardTitle>
              <CardDescription>
                <span className="font-mono">
                  Sell <span className="text-yellow-200">{formatMesos(it.sell_price)}</span>
                  {" • "}Buy <span className="text-orange-300">{formatMesos(it.buy_price)}</span>
                  {it.bonus_per_unit ? <span className="text-emerald-300"> • +{(it.bonus_per_unit * 100).toFixed(2)}% bonus</span> : null}
                </span>
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="item-history-close">
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active special price banner */}
        {specialActive && (
          <div
            className="rounded-lg border-2 border-yellow-500 bg-gradient-to-br from-[#3a2814] to-[#2a1a0e] p-3 shadow-[0_3px_0_#1a0e05]"
            data-testid="item-active-special"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-yellow-300 text-lg">★</span>
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-yellow-300">
                    Active special price
                  </div>
                  <div className="font-mono text-yellow-200">
                    <span className="line-through text-orange-300/60 mr-2">{formatMesos(it.sell_price)}</span>
                    <span className="font-extrabold text-yellow-200 text-base">{formatMesos(it.special_price)}</span>
                    <span className="ml-1 text-xs text-orange-200/80">mesos</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase font-bold tracking-wider text-orange-200/80">Ends at</div>
                <div className="font-mono text-rose-300 text-sm" data-testid="item-active-special-until">
                  {fmtUntil(specialUntilDate)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stock value summary */}
        <div className="grid grid-cols-3 gap-2" data-testid="item-stock-summary">
          <KpiCard
            icon={Boxes}
            label="Current stock"
            value={formatMesos(it.stock || 0)}
            sub="units in store"
            accent="text-amber-300"
            testId="item-kpi-stock"
          />
          <KpiCard
            icon={Coins}
            label="Stock value (sell)"
            value={formatShortMesos(it.stock_value_at_sell || 0) + "m"}
            sub={`${formatMesos(it.stock_value_at_sell || 0)} mesos`}
            accent="text-yellow-300"
            testId="item-kpi-stock-sell"
          />
          <KpiCard
            icon={Coins}
            label="Stock value (buy)"
            value={formatShortMesos(it.stock_value_at_buy || 0) + "m"}
            sub={`${formatMesos(it.stock_value_at_buy || 0)} mesos`}
            accent="text-orange-300"
            testId="item-kpi-stock-buy"
          />
        </div>

        {/* Period blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="item-period-grid">
          <StatBlock title="Selected range" agg={T.range} testId="item-period-range" />
          <StatBlock title="Last 7 days" agg={T.last_7d} testId="item-period-7d" />
          <StatBlock title="Last 30 days" agg={T.last_30d} testId="item-period-30d" />
          <StatBlock title="All-time" agg={T.all_time} testId="item-period-all" />
        </div>

        {/* Price history (derived from orestore_sales) */}
        <div data-testid="item-price-history">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase font-bold tracking-wider text-orange-200/80">
              Price history — sold @ unit price
            </div>
            <div className="text-[10px] text-orange-200/60 italic">
              revenue & profit above already reflect the actual prices charged (incl. promos)
            </div>
          </div>
          {priceHistory.length === 0 ? (
            <div className="rounded-lg border border-[#5a3a14] bg-[#1f1208] p-3 text-xs text-muted-foreground italic">
              No sales recorded for this item yet.
            </div>
          ) : (
            <div className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#3a2814] text-orange-200">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-bold">Unit price</th>
                    <th className="text-left px-2 py-1.5 font-bold">Type</th>
                    <th className="text-right px-2 py-1.5 font-bold">vs base</th>
                    <th className="text-left px-2 py-1.5 font-bold">First seen</th>
                    <th className="text-left px-2 py-1.5 font-bold">Last seen</th>
                    <th className="text-right px-2 py-1.5 font-bold">Qty sold</th>
                    <th className="text-right px-2 py-1.5 font-bold">Revenue</th>
                    <th className="text-right px-2 py-1.5 font-bold">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {priceHistory.map((p) => (
                    <tr
                      key={p.unit_price}
                      className="border-t border-[#5a3a14]/50"
                      data-testid={`item-price-row-${p.unit_price}`}
                    >
                      <td className="px-2 py-1.5 font-mono font-bold text-yellow-200">{formatMesos(p.unit_price)}</td>
                      <td className="px-2 py-1.5">
                        {p.is_promo ? (
                          <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 text-[10px] font-bold uppercase tracking-wider">
                            Promo
                          </span>
                        ) : (
                          <span className="text-orange-200/60 text-[10px] uppercase">Base</span>
                        )}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-mono ${p.diff_pct < 0 ? "text-rose-300" : p.diff_pct > 0 ? "text-emerald-300" : "text-orange-200/60"}`}>
                        {p.diff_pct === 0 ? "—" : (p.diff_pct > 0 ? "+" : "") + p.diff_pct + "%"}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-orange-200/80">{p.first_date}</td>
                      <td className="px-2 py-1.5 font-mono text-orange-200/80">{p.last_date}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-amber-300">{formatMesos(p.qty)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-yellow-300">{formatShortMesos(p.rev)}m</td>
                      <td className="px-2 py-1.5 text-right font-mono text-orange-200/60">{p.tx}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Daily chart */}
        <div>
          <div className="text-[10px] uppercase font-bold tracking-wider text-orange-200/80 mb-1">
            Daily quantity in selected range
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#5a3a14" />
                <XAxis dataKey="label" stroke="#e1b066" fontSize={10} />
                <YAxis stroke="#e1b066" fontSize={10} />
                <Tooltip contentStyle={{ background: "#3a2814", border: "1px solid #5a3a14", color: "#fff" }} />
                <Line type="monotone" dataKey="qty" stroke="#ffae00" strokeWidth={2} dot={{ r: 3 }} name="Quantity" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// =========================== WEEKLY COMPARE PANEL ===========================
const WeeklyComparePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(6);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/orestore/sales/weekly-compare?weeks=${weeks}`,
        { headers: getAuthHeaders() }
      );
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load weekly compare");
    } finally {
      setLoading(false);
    }
  }, [weeks]);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = data?.buckets || [];
  const filteredMatrix = useMemo(() => {
    const matrix = data?.item_matrix || [];
    const q = search.trim().toLowerCase();
    if (!q) return matrix;
    return matrix.filter((it) => (it.name || "").toLowerCase().includes(q));
  }, [data, search]);

  const fmtShort = (n) => {
    const num = Number(n) || 0;
    if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1)}M`;
    if (Math.abs(num) >= 1_000) return `${(num / 1_000).toFixed(0)}k`;
    return String(num);
  };

  const cellColor = (cur, prev) => {
    if (!prev) return "";
    const diff = cur - prev;
    if (diff > 0) return "text-emerald-300";
    if (diff < 0) return "text-red-300";
    return "text-muted-foreground";
  };

  const pctVs = (cur, prev) => {
    if (!prev) return cur ? "+∞" : "0%";
    const v = ((cur - prev) / prev) * 100;
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(v > -10 && v < 10 ? 1 : 0)}%`;
  };

  const chartData = useMemo(() => {
    const bks = data?.buckets || [];
    // Reverse so oldest week is leftmost in the chart
    return [...bks].reverse().map((b) => ({
      label: b.label === "This week" ? "W0" : b.label.replace(" weeks", "w").replace(" week", "w").replace("-", "−"),
      revenue: b.totals?.revenue || 0,
      profit: b.totals?.profit || 0,
      qty: b.totals?.quantity || 0,
    }));
  }, [data]);

  return (
    <Card className="border-2 border-amber-500/30" data-testid="weekly-compare-panel">
      <CardHeader>
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-amber-400" />
              Week-over-week comparison
            </CardTitle>
            <CardDescription>
              Compare recent weeks side-by-side. Tax: {data?.tax_pct ?? 3}%. Buy cost uses current item buy prices.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase font-bold text-muted-foreground tracking-wider">Weeks:</label>
            <select
              value={weeks}
              onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
              className="h-8 px-2 rounded border border-input bg-background text-sm"
              data-testid="weekly-compare-weeks-select"
            >
              {[3, 4, 5, 6, 8, 10, 12].map((n) => (
                <option key={n} value={n}>
                  {n} weeks
                </option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="weekly-compare-refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading weekly buckets…
          </div>
        ) : (
          <>
            {/* Bucket KPI cards */}
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${Math.min(buckets.length, 6)}, minmax(140px, 1fr))`,
                overflowX: "auto",
              }}
              data-testid="weekly-buckets-strip"
            >
              {buckets.map((b, i) => {
                const prev = buckets[i + 1]?.totals;
                const cur = b.totals || {};
                const revPct = pctVs(cur.revenue || 0, prev?.revenue || 0);
                const profPct = pctVs(cur.profit || 0, prev?.profit || 0);
                const isCurrent = i === 0;
                return (
                  <div
                    key={b.from_date}
                    className={`rounded-lg border p-2.5 ${isCurrent ? "border-amber-500 bg-amber-500/5" : "border-border"}`}
                    data-testid={`week-bucket-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase font-extrabold tracking-wider text-muted-foreground">
                        {b.label}
                      </div>
                      {isCurrent && (
                        <Badge className="bg-amber-500/30 text-amber-200 border-amber-500/60 text-[9px]">live</Badge>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground/80 mb-1.5">
                      {b.from_date.slice(5)} → {b.to_date.slice(5)}
                    </div>
                    <div className="space-y-0.5">
                      <div className="flex items-baseline justify-between gap-1">
                        <span className="text-[10px] text-muted-foreground">Rev</span>
                        <span className="font-mono font-bold text-yellow-300">{fmtShort(cur.revenue || 0)}</span>
                      </div>
                      <div className="flex items-baseline justify-between gap-1">
                        <span className="text-[10px] text-muted-foreground">Profit</span>
                        <span className={`font-mono font-bold ${(cur.profit || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {fmtShort(cur.profit || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-1">
                        <span className="text-[10px] text-muted-foreground">Qty</span>
                        <span className="font-mono">{(cur.quantity || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-baseline justify-between gap-1">
                        <span className="text-[10px] text-muted-foreground">Tx</span>
                        <span className="font-mono">{(cur.transactions || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    {prev && (
                      <div className="mt-2 pt-1.5 border-t border-border/40 text-[10px] flex items-center justify-between gap-1">
                        <span className="text-muted-foreground">vs prev:</span>
                        <span className="flex gap-1.5">
                          <span className={cellColor(cur.revenue || 0, prev.revenue || 0)}>R {revPct}</span>
                          <span className={cellColor(cur.profit || 0, prev.profit || 0)}>P {profPct}</span>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Chart */}
            <div className="rounded-lg border border-border p-2 bg-background/40">
              <div className="text-xs uppercase font-bold text-muted-foreground tracking-wider mb-1 px-1">
                Revenue &amp; Profit by Week
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="currentColor" />
                  <YAxis tickFormatter={(v) => fmtShort(v)} tick={{ fontSize: 11 }} stroke="currentColor" />
                  <Tooltip
                    contentStyle={{ background: "#1a1a1a", border: "1px solid #444", fontSize: 12 }}
                    formatter={(v) => fmtShort(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue" fill="#facc15" name="Revenue" />
                  <Bar dataKey="profit" fill="#34d399" name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Per-item matrix */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-sm font-extrabold uppercase tracking-wider text-muted-foreground">
                  Per-item revenue across weeks
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Filter item…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-8 w-44"
                    data-testid="weekly-matrix-search"
                  />
                </div>
              </div>
              {filteredMatrix.length === 0 ? (
                <div className="text-sm text-muted-foreground italic py-4 text-center">
                  No item sales in the selected weeks.
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-bold sticky left-0 bg-muted/40">Item</th>
                        {buckets.map((b) => (
                          <th key={b.from_date} className="text-right px-2 py-1.5 font-bold whitespace-nowrap">
                            {b.label === "This week" ? "W0" : b.label.replace(" weeks", "w").replace(" week", "w").replace("-", "−")}
                          </th>
                        ))}
                        <th className="text-right px-2 py-1.5 font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMatrix.slice(0, 60).map((it) => (
                        <tr
                          key={it.name}
                          className="border-t border-border/30 hover:bg-accent/30"
                          data-testid={`weekly-row-${it.name}`}
                        >
                          <td className="px-2 py-1.5 sticky left-0 bg-background/95">
                            <div className="flex items-center gap-1.5">
                              {it.image_url && (
                                <img src={it.image_url} alt="" className="w-4 h-4 object-contain" style={{ imageRendering: "pixelated" }} />
                              )}
                              <span className="font-bold whitespace-nowrap">{it.name}</span>
                            </div>
                          </td>
                          {it.weekly.map((w, i) => {
                            const prev = it.weekly[i + 1]?.rev || 0;
                            const cur = w.rev || 0;
                            const cls = i < it.weekly.length - 1 ? cellColor(cur, prev) : "";
                            return (
                              <td key={i} className={`text-right px-2 py-1.5 font-mono whitespace-nowrap ${cls}`}>
                                {cur > 0 ? fmtShort(cur) : <span className="text-muted-foreground/40">—</span>}
                              </td>
                            );
                          })}
                          <td className="text-right px-2 py-1.5 font-mono font-extrabold text-yellow-300 whitespace-nowrap">
                            {fmtShort(it.total_rev || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {filteredMatrix.length > 60 && (
                <div className="text-[11px] text-muted-foreground italic mt-2">
                  Showing top 60 of {filteredMatrix.length} items by total revenue.
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// =========================== CONFIG PANEL ===========================
const ConfigPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [webhookInput, setWebhookInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/orestore/config`, { headers: getAuthHeaders() });
      setCfg(r.data);
      setWebhookInput(r.data.discord_webhook || "");
    } catch (e) {
      toast.error("Failed to load OreStore config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/orestore/config`, patch, { headers: getAuthHeaders() });
      toast.success("OreStore config updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/orestore/config/test-webhook`, {}, { headers: getAuthHeaders() });
      toast.success(`Webhook test sent (status ${r.data.status_code}, source: ${r.data.source})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Webhook test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="orestore-config-panel">
      {/* Webhook */}
      <div className="rounded-lg border-2 border-[#5a3a14] bg-[#2a1a0e] p-3 space-y-2">
        <div className="text-sm font-extrabold uppercase tracking-wider text-orange-200">Discord webhook</div>
        <div className="text-xs text-orange-200/80">
          The webhook fires whenever a user submits or schedules an offer.
          {cfg.discord_webhook_env_fallback && (
            <span className="text-yellow-300 ml-1">(currently using <strong>env fallback</strong> — set a value here to override)</span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input
            type="text"
            placeholder="https://discord.com/api/webhooks/…"
            value={webhookInput}
            onChange={(e) => setWebhookInput(e.target.value)}
            className="flex-1 min-w-[220px] font-mono text-xs"
            data-testid="cfg-webhook-input"
          />
          <Button
            onClick={() => save({ discord_webhook: webhookInput })}
            disabled={saving}
            data-testid="cfg-webhook-save"
          >
            Save webhook
          </Button>
          <Button
            variant="outline"
            onClick={sendTest}
            disabled={testing}
            data-testid="cfg-webhook-test"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Send test
          </Button>
        </div>
      </div>

      {/* Other config */}
      <div className="rounded-lg border-2 border-[#5a3a14] bg-[#2a1a0e] p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs uppercase font-bold text-orange-200/80">Max bonus capacity (%)</div>
          <Input
            type="number"
            min="0"
            max="100"
            step="1"
            value={cfg.max_bonus_capacity}
            onChange={(e) => setCfg({ ...cfg, max_bonus_capacity: Number(e.target.value) })}
            onBlur={(e) => save({ max_bonus_capacity: Number(e.target.value) })}
            className="mt-1"
            data-testid="cfg-max-bonus"
          />
        </div>
        <div>
          <div className="text-xs uppercase font-bold text-orange-200/80">Store status</div>
          <Button
            variant={cfg.store_active ? "default" : "destructive"}
            onClick={() => save({ store_active: !cfg.store_active })}
            disabled={saving}
            className="mt-1 w-full"
            data-testid="cfg-store-active"
          >
            {cfg.store_active ? "OPEN" : "CLOSED"} — click to toggle
          </Button>
        </div>
      </div>

      {/* BBCode public URL */}
      <div className="rounded-lg border-2 border-[#5a3a14] bg-[#2a1a0e] p-3 space-y-1">
        <div className="text-sm font-extrabold uppercase tracking-wider text-orange-200">Live BBCode endpoints</div>
        <div className="text-xs text-orange-200/80">
          XenForo BBCode is static — the forum will not auto-pull from MongoDB. To save copy/paste time,
          these public URLs always return the latest BBCode generated from current MongoDB data
          (prices, special_price + special_price_until, bonus ores, guilds). Refresh-paste after price changes.
        </div>
        <div className="flex flex-wrap gap-2 mt-1 font-mono text-xs">
          <a className="underline text-amber-300" href={`${API_URL}/api/orestore/bbcode?mode=buy`} target="_blank" rel="noreferrer" data-testid="cfg-bbc-buy">
            {API_URL}/api/orestore/bbcode?mode=buy
          </a>
          <a className="underline text-amber-300" href={`${API_URL}/api/orestore/bbcode?mode=sell`} target="_blank" rel="noreferrer" data-testid="cfg-bbc-sell">
            {API_URL}/api/orestore/bbcode?mode=sell
          </a>
        </div>
      </div>
    </div>
  );
};

// =========================== MAIN ===========================
const AdminOreStoreDashboard = () => {
  const [tab, setTab] = useState("stock");
  const [stock, setStock] = useState(null);
  const [stockLoading, setStockLoading] = useState(true);

  const [fromDate, setFromDate] = useState(isoDaysAgo(29));
  const [toDate, setToDate] = useState(todayISO());
  const [salesCategoryFilter, setSalesCategoryFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [salesLoading, setSalesLoading] = useState(true);
  const [drillItem, setDrillItem] = useState(null);

  const fetchStock = useCallback(async () => {
    setStockLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/orestore/stock`, {
        headers: getAuthHeaders(),
      });
      setStock(res.data);
    } catch (e) {
      toast.error("Failed to load OreStore stock");
    } finally {
      setStockLoading(false);
    }
  }, []);

  const fetchSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/orestore/sales/summary`, {
        params: { from_date: fromDate, to_date: toDate },
        headers: getAuthHeaders(),
      });
      setSummary(res.data);
    } catch (e) {
      toast.error("Failed to load OreStore sales");
    } finally {
      setSalesLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // Note: heartbeat to /api/admin/orestore/availability/heartbeat is fired
  // globally by <AdminOreStoreHeartbeat /> mounted in App.js, so the admin
  // shows as ONLINE from any page they have open (not only this dashboard).

  const onChangeRange = (f, t) => {
    setFromDate(f);
    setToDate(t);
  };

  return (
    <Card className="bg-card/50" data-testid="admin-orestore-dashboard">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <img src={ORESTORE_LOGO} alt="OreStore" className="h-7 w-auto" />
          <span>OreStore Dashboard</span>
        </CardTitle>
        <CardDescription>Stock health, sales analytics, and config for the OreStore.</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab} data-testid="orestore-dashboard-tabs">
          <TabsList className="grid grid-cols-5 w-full max-w-3xl">
            <TabsTrigger value="stock" data-testid="orestore-tab-stock">
              <Boxes className="w-4 h-4 mr-1" /> Stock
            </TabsTrigger>
            <TabsTrigger value="characters" data-testid="orestore-tab-characters">
              <Users className="w-4 h-4 mr-1" /> Characters
            </TabsTrigger>
            <TabsTrigger value="sales" data-testid="orestore-tab-sales">
              <TrendingUp className="w-4 h-4 mr-1" /> Sales
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="orestore-tab-schedule">
              <CalendarDays className="w-4 h-4 mr-1" /> Schedule
            </TabsTrigger>
            <TabsTrigger value="config" data-testid="orestore-tab-config">
              <Cog className="w-4 h-4 mr-1" /> Config
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stock" className="mt-4">
            <StockPanel
              stock={stock}
              loading={stockLoading}
              onRefresh={fetchStock}
              lowThreshold={stock?.low_threshold || 50}
              onPickItem={setDrillItem}
            />
          </TabsContent>

          <TabsContent value="characters" className="mt-4">
            <CharactersPanel />
          </TabsContent>

          <TabsContent value="sales" className="mt-4 space-y-4">
            <SalesPanel
              summary={summary}
              loading={salesLoading}
              onChangeRange={onChangeRange}
              fromDate={fromDate}
              toDate={toDate}
              onRefresh={fetchSales}
              onPickItem={setDrillItem}
              categoryFilter={salesCategoryFilter}
              setCategoryFilter={setSalesCategoryFilter}
            />
            <WeeklyComparePanel />
          </TabsContent>

          <TabsContent value="schedule" className="mt-4">
            <AdminOreStoreSchedulePanel />
          </TabsContent>

          <TabsContent value="config" className="mt-4">
            <ConfigPanel />
          </TabsContent>
        </Tabs>

        {/* Item drill-down modal (shared by Stock and Sales) */}
        <Dialog open={!!drillItem} onOpenChange={(v) => { if (!v) setDrillItem(null); }}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Ore details: {drillItem}</DialogTitle>
              <DialogDescription>Live stock, sales history, and metadata.</DialogDescription>
            </DialogHeader>
            {drillItem && (
              <ItemHistoryPanel
                name={drillItem}
                fromDate={fromDate}
                toDate={toDate}
                onClose={() => setDrillItem(null)}
              />
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AdminOreStoreDashboard;
